// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { 
  Book, Search, Upload, Brain, TrendingUp, Clock, 
  Users, FileText, Calendar, Star, Activity, Zap,
  ArrowUp, ArrowDown, Eye, Download, Settings
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiJSON } from '../config/api';
import ApiService from '../services/api';
import notificationService from '../utils/notificationService';
import { cn } from '../lib/cn';
import { TwCard } from './ui/TwCard';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalBooks: 0,
    totalPages: 0,
    totalAuthors: 0,
    totalSeries: 0,
    pendingBooks: 0,
    recentUploads: 0,
    searchQueries: 0,
    aiInteractions: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // FIX-004 (refs AUD-003): affichage erreur
  const [quickActions, setQuickActions] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Les métriques métier viennent de /api/library/stats, dont le contrat est
      // explicite. Elles étaient auparavant dérivées de /api/books, un endpoint
      // de PAGINATION : `total_pages` y désigne le nombre de pages de RÉSULTATS
      // et non les pages des livres, et ni `total_pages` ni `total_authors`
      // n'existaient au premier niveau de la réponse — « Pages » et « Auteurs »
      // affichaient donc invariablement 0.
      const [libraryStats, analyticsResponse, notificationsResponse, overviewResponse] = await Promise.all([
        apiJSON('library/stats'),
        apiJSON('analytics/dashboard?days=7').catch(() => ({})),
        apiJSON('notifications?limit=10').catch(() => ({ notifications: [] })),
        ApiService.getSystemOverview().catch(() => ({}))
      ]);
      const overview = overviewResponse?.books ? overviewResponse : {};
      const realStats = {
        totalBooks: libraryStats.total_books ?? 0,
        totalPages: libraryStats.total_pages ?? 0,
        totalAuthors: libraryStats.total_authors ?? 0,
        totalSeries: libraryStats.total_series ?? 0,
        pendingBooks: libraryStats.pending_books ?? 0,
        recentUploads: overview.books?.recent ?? analyticsResponse.recent_uploads ?? 0,
        searchQueries: analyticsResponse.search_queries ?? 0,
        aiInteractions: analyticsResponse.ai_interactions ?? 0
      };
      
      // Convertir les notifications en activité récente
      const realActivity = (notificationsResponse.notifications || []).map(notification => ({
        type: notification.type || 'info',
        title: notification.title || 'Notification',
        description: notification.message || '',
        time: notification.created_at ? new Date(notification.created_at).toLocaleString('fr-FR') : 'Récemment'
      }));
      
      setStats(realStats);
      setRecentActivity(realActivity);
      
      // Définir les actions rapides avec vraies fonctionnalités
      setQuickActions([
        {
          id: 'upload',
          title: 'Importer un livre',
          description: 'Ajouter un nouveau document',
          icon: Upload,
          action: () => window.location.href = '/upload',
          color: 'primary'
        },
        {
          id: 'search',
          title: 'Recherche avancée',
          description: 'Explorer votre bibliothèque',
          icon: Search,
          action: () => window.location.href = '/recherche',
          color: 'secondary'
        },
        {
          id: 'ai-chat',
          title: 'Assistant IA',
          description: 'Poser des questions sur vos livres',
          icon: Brain,
          action: () => window.location.href = '/ia',
          color: 'accent'
        },
        {
          id: 'backup',
          title: 'Sauvegarde',
          description: 'Sauvegarder votre bibliothèque',
          icon: Download,
          action: () => window.location.href = '/gestion',
          color: 'success'
        }
      ]);
      
    } catch (err) {
      console.error('Erreur chargement dashboard:', err);
      setError(err.message || 'Erreur lors du chargement des données');
      notificationService.showToast(err.message || 'Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'upload': return <Upload size={16} />;
      case 'search': return <Search size={16} />;
      case 'ai': return <Brain size={16} />;
      case 'read': return <Eye size={16} />;
      default: return <Activity size={16} />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'upload': return 'var(--success-color)';
      case 'search': return 'var(--primary-color)';
      case 'ai': return 'var(--accent-color)';
      case 'read': return 'var(--secondary-color)';
      default: return 'var(--text-secondary)';
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading flex min-h-[40vh] flex-col items-center justify-center gap-4 p-8 text-neutral-600">
        <div className="loading-spinner" />
        <p className="text-sm">Chargement du tableau de bord...</p>
      </div>
    );
  }

  return (
    <div className="dashboard mx-auto max-w-7xl space-y-8 px-4 py-6 md:px-6">
      {error && (
        <TwCard className="flex flex-wrap items-center justify-between gap-3 border-danger/30 bg-red-50 p-4 text-danger">
          <span>{error}</span>
          <button
            type="button"
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm font-medium text-primary-900 hover:bg-surface-subtle"
            onClick={() => {
              setError(null);
              loadDashboardData();
            }}
          >
            Réessayer
          </button>
        </TwCard>
      )}
      <div className="dashboard-header flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="dashboard-title">
          <h1 className="bg-gradient-to-r from-primary-900 to-accent-500 bg-clip-text text-3xl font-bold text-transparent">
            Tableau de Bord
          </h1>
          <p className="mt-1 text-neutral-600">Vue d&apos;ensemble de votre bibliothèque intelligente</p>
        </div>
        <div className="dashboard-actions">
          {/* Ce bouton n'avait aucun onClick : il ne faisait rien. Il mène
              désormais à l'écran Paramètres, ce qu'il promettait. */}
          <Link
            to="/parametres"
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-primary-900 shadow-soft transition hover:border-accent-500 hover:bg-surface-subtle hover:text-accent-500"
          >
            <Settings size={16} aria-hidden />
            Configuration
          </Link>
        </div>
      </div>

      {/* Métriques principales */}
      <div className="metric-grid grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <TwCard
          className={cn(
            'metric-card flex items-center gap-4 p-6 transition hover:-translate-y-1 hover:shadow-medium'
          )}
        >
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary-900/10 text-primary-900">
            <Book size={24} aria-hidden />
          </div>
          <div className="min-w-0">
            <h3 className="m-0 text-2xl font-bold tabular-nums text-primary-900">{formatNumber(stats.totalBooks)}</h3>
            <p className="m-0 text-sm text-neutral-500">Livres</p>
          </div>
        </TwCard>

        <TwCard
          className={cn(
            'metric-card flex items-center gap-4 p-6 transition hover:-translate-y-1 hover:shadow-medium'
          )}
        >
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary-700/10 text-primary-700">
            <FileText size={24} aria-hidden />
          </div>
          <div className="min-w-0">
            <h3 className="m-0 text-2xl font-bold tabular-nums text-primary-900">{formatNumber(stats.totalPages)}</h3>
            <p className="m-0 text-sm text-neutral-500">Pages</p>
          </div>
        </TwCard>

        <TwCard
          className={cn(
            'metric-card flex items-center gap-4 p-6 transition hover:-translate-y-1 hover:shadow-medium'
          )}
        >
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-accent-500/10 text-accent-600">
            <Users size={24} aria-hidden />
          </div>
          <div className="min-w-0">
            <h3 className="m-0 text-2xl font-bold tabular-nums text-primary-900">{formatNumber(stats.totalAuthors)}</h3>
            <p className="m-0 text-sm text-neutral-500">Auteurs</p>
          </div>
        </TwCard>

        <TwCard
          className={cn(
            'metric-card flex items-center gap-4 p-6 transition hover:-translate-y-1 hover:shadow-medium'
          )}
        >
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-700">
            <Brain size={24} aria-hidden />
          </div>
          <div className="min-w-0">
            <h3 className="m-0 text-2xl font-bold tabular-nums text-primary-900">{formatNumber(stats.aiInteractions)}</h3>
            <p className="m-0 text-sm text-neutral-500">Interactions IA</p>
          </div>
        </TwCard>
      </div>

      {/* Actions rapides */}
      <div className="quick-actions-section">
        <h2>Actions Rapides</h2>
        <div className="quick-actions-grid">
          {quickActions.map((action) => (
            <div 
              key={action.id}
              className={`quick-action-card ${action.color}`}
              onClick={action.action}
            >
              <div className="action-icon">
                <action.icon size={24} />
              </div>
              <div className="action-content">
                <h3>{action.title}</h3>
                <p>{action.description}</p>
              </div>
              <div className="action-arrow">
                <ArrowUp size={16} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Activité récente et graphiques */}
      <div className="dashboard-content">
        <div className="content-left">
          <div className="recent-activity">
            <div className="section-header">
              <h2>Activité Récente</h2>
              {/* Correctif audit P3 : bouton « Voir tout » retiré (aucune action câblée). */}
            </div>
            
            <div className="activity-list">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, index) => (
                  <div key={index} className="activity-item">
                    <div 
                      className="activity-icon"
                      style={{ color: getActivityColor(activity.type) }}
                    >
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="activity-content">
                      <p className="activity-title">{activity.title}</p>
                      <p className="activity-description">{activity.description}</p>
                      <span className="activity-time">{activity.time}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <Activity size={48} />
                  <p>Aucune activité récente</p>
                  <small>Vos actions apparaîtront ici</small>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="content-right">
          <div className="stats-overview">
            <div className="section-header">
              <h2>Statistiques</h2>
              {/* Correctif audit P3 : bouton « Détails » retiré (aucune action câblée). */}
            </div>
            
            {/* Correctif audit P1-11 : valeurs branchées sur les stats réelles
                (stats.*) au lieu de nombres codés en dur. Les métriques sans
                source réelle (temps de lecture, favoris, performance système,
                état du système) ont été retirées plutôt que fabriquées. */}
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-label">
                  <Search size={16} />
                  <span>Recherches</span>
                </div>
                <div className="stat-value">{formatNumber(stats.searchQueries)}</div>
                <div className="stat-period">7 derniers jours</div>
              </div>

              <div className="stat-item">
                <div className="stat-label">
                  <Upload size={16} />
                  <span>Imports récents</span>
                </div>
                <div className="stat-value">{formatNumber(stats.recentUploads)}</div>
                <div className="stat-period">7 derniers jours</div>
              </div>

              <div className="stat-item">
                <div className="stat-label">
                  <Brain size={16} />
                  <span>Interactions IA</span>
                </div>
                <div className="stat-value">{formatNumber(stats.aiInteractions)}</div>
                <div className="stat-period">7 derniers jours</div>
              </div>

              <div className="stat-item">
                <div className="stat-label">
                  <Book size={16} />
                  <span>Livres</span>
                </div>
                <div className="stat-value">{formatNumber(stats.totalBooks)}</div>
                <div className="stat-period">Total</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

